import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircleOutline,
  CheckCircleRounded,
  LocalHospitalRounded,
  ShieldRounded,
  DirectionsCarFilledRounded,
} from '@mui/icons-material'
import Logo from '../assets/images/ambunetLogo.png'
import SignUpForm from './SignUpForm'
import SignUpStepper from './SignUpStepper'
import { signupCardSx, signupPalette } from './signupTheme'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import '../components/Fonts.css'
import '../app.css'

const featureItems = [
  {
    icon: <LocalHospitalRounded fontSize="small" />,
    text: 'Patient records, planning, fleet, governance and operations in one place',
  },
  {
    icon: <ShieldRounded fontSize="small" />,
    text: 'Built for compliant, real-world use by ambulance services and medical providers',
  },
  {
    icon: <DirectionsCarFilledRounded fontSize="small" />,
    text: 'Manage your service from booking to deployment with one connected platform',
  },
]

const LandingSignupPage = () => {
  const [showSuccessPage, setShowSuccessPage] = useState(false)
  const [formStep, setFormStep] = useState(0)

  const theme = { palette: signupPalette }

  // Wizard: 0 details, 1 plan (inside the form) — once submitted the user is
  // on step 2 (verify email).
  const activeStep = showSuccessPage ? 2 : formStep

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#F7F9FC',
        background: 'linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            style={{ width: '100%', display: 'flex' }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 5,
                overflow: 'hidden',
                px: { xs: 3, md: 5 },
                py: { xs: 4, md: 5 },
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: 'white',
                boxShadow: '0 20px 60px rgba(36,47,64,0.16)',
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt="AmbuNet Logo Background"
                sx={{
                  position: 'absolute',
                  right: { xs: -40, md: -20 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: { xs: '90%', md: '78%' },
                  maxWidth: 560,
                  opacity: 0.06,
                  zIndex: 0,
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />

              <Stack
                spacing={3}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: 620,
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    flexWrap="wrap"
                  >
                    <Box
                      component="img"
                      src={Logo}
                      alt="AmbuNet Logo"
                      sx={{
                        width: { xs: 52, md: 64 },
                        height: 'auto',
                        filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.12))',
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: { xs: '1.6rem', md: '2rem' },
                        letterSpacing: '-0.03em',
                        lineHeight: 1,
                      }}
                      fontFamily="moonlander"
                    >
                      AmbuNet
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      fontSize: { xs: '2.2rem', md: '2.6rem' },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: '-0.04em',
                      maxWidth: 580,
                    }}
                  >
                    Run your medical operations from one platform
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      lineHeight: 1.75,
                      color: 'rgba(255,255,255,0.84)',
                      maxWidth: 560,
                    }}
                  >
                    AmbuNet helps ambulance services and professional medical
                    providers manage patients, staff, vehicles, governance, and
                    operational planning in one connected system.
                  </Typography>
                </Stack>

                <Stack spacing={1.4}>
                  {featureItems.map((item) => (
                    <Stack
                      key={item.text}
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255,255,255,0.14)',
                          color: theme.palette.third.light,
                          flexShrink: 0,
                          mt: '2px',
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.92)',
                          lineHeight: 1.6,
                          fontSize: { xs: '0.98rem', md: '1rem' },
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 1,
                    p: 2.25,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.10)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#FFFFFF',
                      }}
                    >
                      Ready when you are
                    </Typography>

                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.82)',
                        lineHeight: 1.65,
                        fontSize: '0.95rem',
                      }}
                    >
                      Choose your plan, pay securely with Stripe, and your
                      organisation&apos;s AmbuNet system is provisioned in
                      minutes — ready for your first login.
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </motion.div>
        </Box>

        <Box sx={{ display: 'flex' }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            style={{ width: '100%', display: 'flex' }}
          >
            <Box
              component={Paper}
              elevation={0}
              sx={{
                ...signupCardSx,
                justifyContent: 'flex-start',
                p: { xs: 2.5, md: 4 },
              }}
            >
              <SignUpStepper activeStep={activeStep} />

              {showSuccessPage ? (
                <Stack
                  spacing={2.5}
                  alignItems="center"
                  textAlign="center"
                  sx={{ my: 'auto' }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      bgcolor: 'rgba(35,154,164,0.10)',
                      color: theme.palette.third.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircleOutline fontSize="large" />
                  </Box>

                  <Stack spacing={1}>
                    <Typography
                      sx={{
                        fontSize: { xs: '1.6rem', md: '2rem' },
                        fontWeight: 800,
                        color: theme.palette.primary.main,
                        letterSpacing: '-0.03em',
                      }}
                    >
                      Check your inbox
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 460,
                        color: 'text.secondary',
                        lineHeight: 1.75,
                      }}
                    >
                      We&apos;ve sent you an email to verify your address.
                      Once verified, you&apos;ll accept the AmbuNet Business
                      Subscription Agreement, complete secure payment and
                      your new AmbuNet system will be created straight away.
                    </Typography>
                  </Stack>

                  <Divider flexItem sx={{ my: 1 }} />

                  <Stack spacing={1} alignItems="center">
                    {[
                      'Open the email we’ve sent you',
                      'Click the verification link',
                      'Review and accept the Business Subscription Agreement',
                      'Complete secure payment with Stripe',
                      'Sign in to your new AmbuNet system',
                    ].map((text) => (
                      <Stack
                        key={text}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <CheckCircleRounded
                          sx={{
                            color: theme.palette.third.main,
                            fontSize: 18,
                          }}
                        />

                        <Typography variant="body2" color="text.secondary">
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              ) : (
                <SignUpForm
                  showSuccessPage={showSuccessPage}
                  setShowSuccessPage={setShowSuccessPage}
                  onStepChange={setFormStep}
                />
              )}
            </Box>
          </motion.div>
        </Box>
      </Box>
    </Box>
  )
}

export default LandingSignupPage
